import React, { useState } from 'react';

interface UserSearchFormProps {
  onSearch: (query: string, plan?: string) => void;
  loading: boolean;
}

const UserSearchForm: React.FC<UserSearchFormProps> = ({ onSearch, loading }) => {
  const [query, setQuery] = useState('');
  const [selectedPlan, setSelectedPlan] = useState<string>('');

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (query.trim()) {
      onSearch(query.trim(), selectedPlan || undefined);
    }
  };

  const planOptions = [
    { value: '', label: 'All Plans' },
    { value: 'free', label: 'Free' },
    { value: 'creator-lite', label: 'Creator Lite' },
    { value: 'creator-pro', label: 'Creator Pro' },
    { value: 'creator-unlimited', label: 'Creator Unlimited' }
  ];

  return (
    <div style={{ 
      background: 'rgba(15, 23, 42, 0.8)', 
      border: '1px solid rgba(71, 85, 105, 0.3)',
      borderRadius: '12px',
      padding: '1.5rem'
    }}>
      <h3 style={{ 
        color: '#e5e7eb', 
        marginBottom: '1rem', 
        fontSize: '1.1rem',
        display: 'flex',
        alignItems: 'center',
        gap: '0.5rem'
      }}>
        🔍 Search Users
      </h3>

      <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
        {/* Search Input */}
        <div>
          <label style={{ 
            display: 'block', 
            color: '#9ca3af', 
            fontSize: '0.875rem', 
            marginBottom: '0.5rem' 
          }}>
            Email or User ID
          </label>
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Enter email or user ID..."
            disabled={loading}
            style={{
              width: '100%',
              padding: '0.75rem',
              background: 'rgba(30, 41, 59, 0.8)',
              border: '1px solid rgba(71, 85, 105, 0.5)',
              borderRadius: '8px',
              color: '#e5e7eb',
              fontSize: '0.875rem',
              outline: 'none',
              opacity: loading ? 0.6 : 1
            }}
          />
        </div>

        {/* Plan Filter */}
        <div>
          <label style={{ 
            display: 'block', 
            color: '#9ca3af', 
            fontSize: '0.875rem', 
            marginBottom: '0.5rem' 
          }}>
            Filter by Plan
          </label>
          <select
            value={selectedPlan}
            onChange={(e) => setSelectedPlan(e.target.value)}
            disabled={loading}
            style={{
              width: '100%',
              padding: '0.75rem',
              background: 'rgba(30, 41, 59, 0.8)',
              border: '1px solid rgba(71, 85, 105, 0.5)',
              borderRadius: '8px',
              color: '#e5e7eb',
              fontSize: '0.875rem',
              outline: 'none',
              opacity: loading ? 0.6 : 1
            }}
          >
            {planOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>

        {/* Search Button */}
        <button
          type="submit"
          disabled={loading || !query.trim()}
          style={{
            padding: '0.75rem 1.5rem',
            background: loading || !query.trim() 
              ? 'rgba(71, 85, 105, 0.5)' 
              : 'linear-gradient(135deg, #06b6d4, #3b82f6)',
            color: 'white',
            border: 'none',
            borderRadius: '8px',
            cursor: loading || !query.trim() ? 'not-allowed' : 'pointer',
            fontSize: '0.875rem',
            fontWeight: '500',
            transition: 'all 0.2s',
            opacity: loading || !query.trim() ? 0.6 : 1
          }}
        >
          {loading ? 'Searching...' : 'Search Users'}
        </button>
      </form>

      {/* Quick Actions */}
      <div style={{ 
        marginTop: '1rem', 
        paddingTop: '1rem', 
        borderTop: '1px solid rgba(71, 85, 105, 0.3)' 
      }}>
        <div style={{ color: '#9ca3af', fontSize: '0.75rem', marginBottom: '0.5rem' }}>
          Quick Actions:
        </div>
        <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
          <QuickSearchButton 
            label="High Usage Users" 
            onClick={() => onSearch('high-usage', '')}
            disabled={loading}
          />
          <QuickSearchButton 
            label="Unlimited Users" 
            onClick={() => onSearch('', 'creator-unlimited')}
            disabled={loading}
          />
          <QuickSearchButton 
            label="Low Balance" 
            onClick={() => onSearch('low-balance', '')}
            disabled={loading}
          />
        </div>
      </div>
    </div>
  );
};

interface QuickSearchButtonProps {
  label: string;
  onClick: () => void;
  disabled: boolean;
}

const QuickSearchButton: React.FC<QuickSearchButtonProps> = ({ label, onClick, disabled }) => (
  <button
    onClick={onClick}
    disabled={disabled}
    style={{
      padding: '0.25rem 0.75rem',
      background: 'rgba(71, 85, 105, 0.3)',
      color: '#9ca3af',
      border: '1px solid rgba(71, 85, 105, 0.5)',
      borderRadius: '6px',
      fontSize: '0.75rem',
      cursor: disabled ? 'not-allowed' : 'pointer',
      opacity: disabled ? 0.5 : 1,
      transition: 'all 0.2s'
    }}
  >
    {label}
  </button>
);

export default UserSearchForm;
